import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { ApiError, apiErrorCode } from "@/lib/api";
import { buttonClass } from "@/components/buttonStyles";
import { QuoteCard } from "@/components/submissions/QuoteCard";
import { MissingFieldsList } from "@/components/submissions/MissingFieldsList";
import { QuotePopup } from "@/components/submissions/QuotePopup";
import { QuotesLoading } from "@/components/submissions/QuotesLoading";
import {
  carrierStepError,
  missingFieldsOf,
  requestCarrierQuote,
  requestPriceIndication,
  type BrokerQuote,
  type DeclinedCarrier,
  type SubmissionCarrier,
  type CarrierIndication,
  type MissingField,
} from "@/lib/submissions/brokerSubmissions";
import { messageFor } from "@/lib/errors";
import {
  firmResultLine,
  problemLine,
  quoteSignature,
  quotesOnTheirWay,
  requestedTerms,
  toQuoteCards,
  type FirmRun,
  type FirmRuns,
  type QuoteCard as Card,
} from "@/lib/submissions/quoteCards";
import { coveragePartsOf } from "@/lib/submissions/coverageRows";
import { COVERAGE_TYPES } from "@/lib/submissions/coverageTerms";
import { useQuotePopup } from "@/lib/submissions/useQuotePopup";
import { utcTodayIso } from "@/lib/dates";

/** How often, and how many times, a quote waiting on PMS is read again:
 *  every 20 seconds for four minutes. */
const WAITING_READ_MS = 20_000;
const WAITING_READS = 12;

// BRK-S049 → BRK-S347 — the markets' quotes for a submission, as cards.
// One indication request FANS OUT server-side across every eligible carrier
// and returns per-carrier results (PMS-S143): a priced carrier is an offer;
// a referred, declined or unavailable one is information the broker wants,
// shown beside the offers with its reasons, never an error that blanks them.
//
// - The indication is AUTO-PULLED once the submission is quote-ready with no
//   offers yet; duplicate fires are guarded. Not-ready (400, with its missing
//   fields) and error states are handled (NN #7).
// - Each card opens the quote popup (URL-addressed, useQuotePopup): the
//   offer's details, and its one action.
// - Quotes go per carrier, from its popup (Nav, 2026-10-12, replacing
//   BRK-S298's tick-and-Continue): its questions, then its price. A run lives
//   here, not in the popup, so it goes on when the popup closes and the page
//   shows it; nothing about it is stored (NN #9).
// - While any quote is being fetched, a loading screen stands in for the
//   cards (BRK-S460), so an earlier or outdated quote and its way to ask
//   again never read as the answer meanwhile.
// - A Workers' Comp submission's carriers are asked on the wizard's last step
//   (BRK-S393, `questionsHref`): the popup prices only, and a carrier with
//   questions left sends the broker there. The quotes Submit had PMS record
//   (PMS-S815, `requestQuotesFor`) are requested all at once as the page
//   opens; one that doesn't come back is PMS's job to retry, so the page reads
//   the quotes again for a while, never showing a quote PMS hasn't sent.

export function QuoteComparison({
  submissionId,
  accountId = null,
  quotes,
  onQuotesChanged,
  quoteFloorMissing = [],
  onCompleteProfile,
  initialOutcomes,
  missingFieldHref,
  popupBlocked = false,
  onFirmUpChange,
  declined = [],
  requestQuotesFor = [],
  questionsHref,
  carriers = [],
  titleVisible = true,
}: {
  submissionId: string;
  /** The insured's account, whose documents serve the files that answer a
   *  quote's subjectivities (BRK-S362). */
  accountId?: string | null;
  quotes: BrokerQuote[];
  onQuotesChanged: () => void | Promise<void>;
  /** The account-scope QUOTE-floor fields the insured doesn't yet satisfy
   *  (BRK-S057). When non-empty, a quote is GATED: the broker completes
   *  the profile first instead of firing a request the server would reject. */
  quoteFloorMissing?: MissingField[];
  /** Open the page's insured-profile completion form. */
  onCompleteProfile?: () => void;
  /** A fan-out already run for this visit, e.g. by the WC wizard's Submit
   *  (BRK-S305): shown as it came back, without pulling another. */
  initialOutcomes?: CarrierIndication[];
  /** Where a missing field is asked, so a needs-information carrier links to
   *  it; null for a field the page can't place. */
  missingFieldHref?: (field: MissingField) => string | null;
  /** Another panel holds the page, such as Preview submission (BRK-S357): no
   *  quote's popup opens over it, even when the URL names one. */
  popupBlocked?: boolean;
  /** Told whether any carrier can be firmed up now, so the page can say a
   *  quote is next (BRK-S358). */
  onFirmUpChange?: (canFirmUp: boolean) => void;
  declined?: DeclinedCarrier[];
  /** The carriers whose quote Submit had PMS record (BRK-S393): requested
   *  in parallel, once, as the comparison opens. */
  requestQuotesFor?: readonly { carrierId: string; carrierName: string }[];
  /** Where a carrier's questions are asked, when not in the popup: the WC
   *  wizard's last step (BRK-S393). */
  questionsHref?: string;
  /** The carriers in play: a carrier Signal quotes by hand has a card of its
   *  own until its quote is in (BRK-S395). */
  carriers?: readonly SubmissionCarrier[];
  /** False inside a tab that already names the section: the heading is then
   *  for assistive tech alone (BRK-S467). */
  titleVisible?: boolean;
}) {
  const { authedRequest } = useAuth();
  const navigate = useNavigate();
  const headingId = useId();

  const [pulling, setPulling] = useState(false);
  const [notReady, setNotReady] = useState<string | null>(null);
  // The structured missing-fields list off a quote-readiness 400 (PMS-S144) —
  // rendered as a field-level checklist instead of the raw message (BRK-S052).
  const [missingFields, setMissingFields] = useState<MissingField[] | null>(
    null,
  );
  const [pullError, setPullError] = useState<string | null>(null);
  // The per-carrier fan-out results (BRK-S053).
  const [outcomes, setOutcomes] = useState<CarrierIndication[] | null>(
    initialOutcomes ?? null,
  );
  // This visit's quotes, by card (BRK-S347). Submit's are on their way from
  // the first paint (BRK-S460), so no earlier quote shows before they're asked.
  const [runs, setRuns] = useState<FirmRuns>(() =>
    Object.fromEntries(
      requestQuotesFor.map((c): [string, FirmRun] => [
        c.carrierId,
        { kind: "pricing", carrierId: c.carrierId, carrier: c.carrierName },
      ]),
    ),
  );
  // A run that finishes while its popup is closed is announced.
  const [announcement, setAnnouncement] = useState("");

  const cards = useMemo(
    () =>
      toQuoteCards(quotes, outcomes, runs, declined, utcTodayIso(), carriers),
    [quotes, outcomes, runs, declined, carriers],
  );
  // While quotes are being fetched, the loading screen holds the cards and
  // their actions (BRK-S460).
  const onTheirWay = quotesOnTheirWay(cards);
  const termGroups = requestedTerms(cards);
  const coverageTypes = COVERAGE_TYPES.filter((type) =>
    cards.some((card) =>
      coveragePartsOf(card.quote?.terms).some(
        (part) => part.coverageType === type,
      ),
    ),
  );
  const anyFirmUp = cards.some((c) => c.canFirmUp);
  useEffect(() => {
    onFirmUpChange?.(anyFirmUp);
  }, [anyFirmUp, onFirmUpChange]);
  const popup = useQuotePopup();
  const openKey = popupBlocked ? null : popup.key;
  const openCard =
    openKey === null ? null : (cards.find((c) => c.key === openKey) ?? null);

  // Auto-pull the indication exactly once per submission, when quote-ready with
  // no offers yet. The fire is guarded by a per-submission ref (not the effect
  // cleanup), so a re-render from an unstable authedRequest / onQuotesChanged
  // never cancels the in-flight pull — reading the latest values off refs keeps
  // them out of the dependency array. A mounted ref avoids setState-after-unmount.
  const pulledFor = useRef<string | null>(
    initialOutcomes ? submissionId : null,
  );
  const mounted = useRef(true);
  const quotesRef = useRef(quotes);
  quotesRef.current = quotes;
  const changedRef = useRef(onQuotesChanged);
  changedRef.current = onQuotesChanged;

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  function setRun(key: string, run: FirmRun) {
    const carrier = cards.find((c) => c.key === key)?.carrier;
    setRuns((all) => ({ ...all, [run.carrierId || key]: { ...run, carrier } }));
  }

  /** After the broker acts on a quote in its popup (select, unselect, a bind
   *  or review request), the quotes PMS read back say where it stands: the
   *  copy this visit's request came back with stops speaking for it, or the
   *  card reads Quote until a reload (BRK-S506; Nav, 2026-10-29). */
  async function quoteChanged(card: Card | null) {
    await changedRef.current();
    const carrierId = card?.column.carrierId;
    if (!card || !card.quote) return;
    setRuns((all) =>
      Object.fromEntries(
        Object.entries(all).filter(
          ([key, r]) =>
            r.kind !== "done" ||
            (carrierId ? r.carrierId !== carrierId : key !== card.key),
        ),
      ),
    );
  }

  // One fan-out across the eligible markets (PMS-S143). Its results live only
  // in this visit, so after a reload the markets are checked again on request.
  // A fresh fan-out replaces this visit's finished quotes; one still
  // being answered or priced goes on.
  async function pull() {
    setPulling(true);
    setNotReady(null);
    setMissingFields(null);
    setPullError(null);
    setOutcomes(null);
    setRuns((all) =>
      Object.fromEntries(
        Object.entries(all).filter(
          ([, r]) => r.kind === "answering" || r.kind === "pricing",
        ),
      ),
    );
    try {
      const res = await requestPriceIndication(authedRequest, submissionId);
      if (mounted.current) setOutcomes(res.results);
      await changedRef.current();
    } catch (err) {
      if (!mounted.current) return;
      if (err instanceof ApiError && err.status === 400) {
        setNotReady(messageFor(err));
        setMissingFields(missingFieldsOf(err));
      } else setPullError(messageFor(err));
    } finally {
      if (mounted.current) setPulling(false);
    }
  }
  const pullRef = useRef(pull);
  pullRef.current = pull;

  useEffect(() => {
    if (pulledFor.current === submissionId || quotesRef.current.length > 0)
      return;
    pulledFor.current = submissionId;
    void pullRef.current();
  }, [submissionId]);

  // The carrier whose questions are on screen, to show a refusal in place.
  const watching = useRef<string | null>(null);

  /** Starts, continues or retries a carrier's quote in its popup. With
   *  its questions asked elsewhere, the popup prices it, and a declined
   *  carrier's answers change where they're asked. */
  function firmUp(card: Card) {
    const carrierId = card.column.carrierId;
    if (!carrierId) return;
    if (questionsHref) {
      if (card.chip.label === "Declined") navigate(questionsHref);
      else void quote(card).catch(() => undefined);
      return;
    }
    if (card.run?.kind !== "answering")
      setRun(card.key, { kind: "answering", carrierId, note: null });
  }

  /** The carrier's questions are complete: its quote, from the answers
   *  saved through the loop (BRK-S290). A refusal goes back to the questions
   *  when they're on screen; otherwise it waits on the card. */
  async function quote(card: Card): Promise<void> {
    const carrierId = card.column.carrierId;
    if (!carrierId) return;
    const since = quoteSignature(quotesRef.current, carrierId);
    setRun(card.key, { kind: "pricing", carrierId });
    try {
      const result = await requestCarrierQuote(
        authedRequest,
        submissionId,
        carrierId,
      );
      if (!mounted.current) return;
      setRun(card.key, { kind: "done", carrierId, result });
      if (watching.current !== card.key)
        setAnnouncement(firmResultLine(card.carrier, result));
      await changedRef.current();
    } catch (err) {
      if (!mounted.current) throw err;
      if (await settledElsewhere(err, carrierId, card.carrier, since)) return;
      const problem = carrierStepError(err, "pricing");
      // Questions left go to where they're asked.
      if (questionsHref && problem.kind === "incomplete") {
        navigate(questionsHref);
        return;
      }
      if (questionsHref) {
        setRun(card.key, { kind: "failed", carrierId, problem });
        setAnnouncement(`${card.carrier}’s quote didn’t go through.`);
        return;
      }
      if (watching.current === card.key) {
        setRun(card.key, { kind: "answering", carrierId, note: null });
        throw err;
      }
      setRun(
        card.key,
        problem.kind === "incomplete" || problem.kind === "fieldErrors"
          ? {
              kind: "answering",
              carrierId,
              note:
                problem.kind === "incomplete"
                  ? `${card.carrier} has a few more questions — answer them to continue.`
                  : `${problemLine(problem, card.carrier)} Check the answers and try again.`,
            }
          : { kind: "failed", carrierId, problem },
      );
      setAnnouncement(`${card.carrier}’s quote didn’t go through.`);
    }
  }

  /** A recorded quote request PMS says another caller holds is on its way
   *  until a newer quote is read back; one answered meanwhile is simply read
   *  (PMS-S815). Neither is a refusal, wherever it was asked (BRK-S460).
   *  False for any other error. */
  async function settledElsewhere(
    err: unknown,
    carrierId: string,
    carrier: string,
    since: string | null,
  ): Promise<boolean> {
    const code = apiErrorCode(err);
    if (code === "quote_already_requested") {
      setRuns((all) => {
        const next = { ...all };
        delete next[carrierId];
        return next;
      });
      await changedRef.current();
      return true;
    }
    if (code !== "quote_request_in_progress") return false;
    setRuns((all) => ({
      ...all,
      [carrierId]: {
        kind: "waiting",
        carrierId,
        carrier,
        since,
        inProgress: true,
      },
    }));
    return true;
  }

  // The quotes Submit had PMS record (PMS-S815), each requested at once, in
  // parallel, exactly once per submission. One that failed waits on PMS: the
  // card says so until a newer quote is read back.
  const requestedFor = useRef<string | null>(null);
  async function requestSubmitted(carrierId: string, carrier: string) {
    const since = quoteSignature(quotesRef.current, carrierId);
    setRuns((all) => ({
      ...all,
      [carrierId]: { kind: "pricing", carrierId, carrier },
    }));
    try {
      const result = await requestCarrierQuote(
        authedRequest,
        submissionId,
        carrierId,
      );
      if (!mounted.current) return;
      setRuns((all) => ({
        ...all,
        [carrierId]: { kind: "done", carrierId, carrier, result },
      }));
      await changedRef.current();
    } catch (err) {
      if (!mounted.current) return;
      if (await settledElsewhere(err, carrierId, carrier, since)) return;
      setRuns((all) => ({
        ...all,
        [carrierId]: {
          kind: "waiting",
          carrierId,
          carrier,
          since,
          inProgress: false,
        },
      }));
    }
  }
  const requestRef = useRef(requestSubmitted);
  requestRef.current = requestSubmitted;
  const handed = useRef(requestQuotesFor);
  useEffect(() => {
    if (requestedFor.current === submissionId) return;
    requestedFor.current = submissionId;
    for (const c of handed.current)
      void requestRef.current(c.carrierId, c.carrierName);
  }, [submissionId]);

  // While a carrier's quote waits on PMS, read the quotes again now and then,
  // for as long as PMS's job keeps trying (PMS-S815: open after two minutes,
  // then every minute).
  const waiting = Object.values(runs).some((r) => r.kind === "waiting");
  useEffect(() => {
    if (!waiting) return;
    let reads = 0;
    const timer = setInterval(() => {
      reads += 1;
      if (reads <= WAITING_READS) {
        void changedRef.current();
        return;
      }
      clearInterval(timer);
      // The page stops watching: a request another caller held reads as
      // PMS still trying, so the loading screen never outlasts the reads
      // (BRK-S460).
      setRuns((all) =>
        Object.fromEntries(
          Object.entries(all).map(([key, r]) => [
            key,
            r.kind === "waiting" ? { ...r, inProgress: false } : r,
          ]),
        ),
      );
    }, WAITING_READ_MS);
    return () => clearInterval(timer);
  }, [waiting]);
  // A newer quote read back ends the wait.
  useEffect(() => {
    setRuns((all) => {
      const answered = Object.entries(all).filter(
        ([, r]) =>
          r.kind === "waiting" &&
          quoteSignature(quotes, r.carrierId) !== r.since,
      );
      if (answered.length === 0) return all;
      const next = { ...all };
      for (const [key] of answered) delete next[key];
      return next;
    });
  }, [quotes]);

  // Closing the popup puts focus back on the card it was opened from.
  const cardButtons = useRef(new Map<string, HTMLButtonElement>());
  const lastOpen = useRef<string | null>(null);
  useEffect(() => {
    if (openKey !== null) {
      lastOpen.current = openKey;
      return;
    }
    const was = lastOpen.current;
    lastOpen.current = null;
    if (was) cardButtons.current.get(was)?.focus({ preventScroll: true });
  }, [openKey]);

  // The quote-stage account floor isn't met — every quote would 400
  // (BRK-S057), so the broker completes the profile first.
  const floorBlocked = quoteFloorMissing.length > 0;
  const completeProfile = onCompleteProfile
    ? () => popup.closeThen(onCompleteProfile)
    : undefined;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col">
        {/* Inside the WC page's Quotes tab the tab already names the section
            (BRK-S464), so the heading is for assistive tech alone (BRK-S467);
            the older GL page still shows it. */}
        <h2
          id={headingId}
          className={titleVisible ? "text-heading text-brk-text" : "sr-only"}
        >
          {titleVisible ? "Pricing & quotes" : "Quotes"}
        </h2>
        <p className="text-sm text-brk-dim">
          Pricing from our markets. Open a carrier for its details or a quote.
        </p>
      </div>
      {outcomes === null &&
        !pulling &&
        quotes.length > 0 &&
        onTheirWay.length === 0 && (
          <button
            type="button"
            onClick={() => void pull()}
            className={`${buttonClass({ variant: "secondary", size: "sm" })} self-start`}
          >
            Check the markets again
          </button>
        )}

      {pulling && (
        <p role="status" className="text-sm text-brk-dim">
          Getting price indications…
        </p>
      )}
      {notReady && (
        <div
          role="status"
          className="rounded-md border border-brk-border bg-brk-bg px-3 py-2 text-sm text-brk-dim"
        >
          This submission needs a few more details before we can price it.
          {missingFields ? (
            <MissingFieldsList
              missing={missingFields}
              missingFieldHref={missingFieldHref}
              accountHint
              className="mt-1 list-inside list-disc"
            />
          ) : (
            ` ${notReady}`
          )}
        </div>
      )}
      {pullError && (
        <p role="alert" className="text-sm text-brk-danger">
          {pullError}
        </p>
      )}
      {outcomes && outcomes.length === 0 && cards.length === 0 && (
        <div className="rounded-md border border-brk-border bg-brk-bg px-3 py-2 text-sm text-brk-dim">
          No markets are currently available for this product — our team will
          follow up on this submission.
        </div>
      )}

      {onTheirWay.length > 0 ? (
        <QuotesLoading carriers={onTheirWay} placeholders={cards.length} />
      ) : (
        cards.length > 0 && (
          <ul
            role="list"
            aria-labelledby={headingId}
            className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3"
          >
            {cards.map((card) => (
              <li key={card.key} className="min-w-0">
                <QuoteCard
                  card={card}
                  onOpen={() => popup.open(card.key)}
                  buttonRef={(el) => {
                    if (el) cardButtons.current.set(card.key, el);
                    else cardButtons.current.delete(card.key);
                  }}
                />
              </li>
            ))}
          </ul>
        )
      )}

      {floorBlocked &&
        onTheirWay.length === 0 &&
        cards.some((c) => c.canFirmUp) && (
          <div className="flex flex-col gap-1">
            <button
              type="button"
              onClick={onCompleteProfile}
              className={`${buttonClass({ size: "sm" })} self-start`}
            >
              Complete the insured profile to quote
            </button>
            <span className="text-xs text-brk-dim">
              A quote needs: {quoteFloorMissing.map((f) => f.label).join(", ")}.
            </span>
          </div>
        )}

      <p role="status" className="sr-only">
        {announcement}
      </p>

      {openKey !== null && (
        <QuotePopup
          key={openKey}
          submissionId={submissionId}
          accountId={accountId}
          onQuotesChanged={() => quoteChanged(openCard)}
          card={openCard}
          pending={pulling}
          termGroups={termGroups}
          coverageTypes={coverageTypes}
          floorMissing={quoteFloorMissing}
          pulling={pulling}
          onClose={popup.close}
          onFirmUp={firmUp}
          onComplete={quote}
          onRetry={firmUp}
          onWatch={(key) => {
            watching.current = key;
          }}
          onDeclineChange={(card, decline) => {
            const carrierId = card.column.carrierId;
            if (carrierId)
              setRun(card.key, {
                kind: "answering",
                carrierId,
                note: null,
                declined: decline,
              });
            if (decline && watching.current !== card.key)
              setAnnouncement(`${card.carrier} declined this application.`);
          }}
          onUpdate={() => void pull()}
          onCompleteProfile={completeProfile}
          missingFieldHref={missingFieldHref}
          asks={!questionsHref}
        />
      )}
    </div>
  );
}
